import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@eduviaos/ui-kit";
import { EASE_OUT } from "../../lib/motion";
import type { SeatAvailabilityItem } from "../../lib/seat/api";

export function SeatMapGrid({
  seats,
  selectedId,
  onSelect,
  zoneNames,
  strings,
}: {
  seats: SeatAvailabilityItem[];
  selectedId: string | null;
  onSelect: (seatId: string) => void;
  zoneNames?: Map<string, string>;
  strings: {
    seatTaken: string;
    seatAvailable: string;
    yourSeat: string;
  };
}) {
  const reduce = useReducedMotion();
  const grouped = React.useMemo(() => {
    const byZone = new Map<string, SeatAvailabilityItem[]>();
    for (const seat of seats) {
      const list = byZone.get(seat.zone_id) ?? [];
      list.push(seat);
      byZone.set(seat.zone_id, list);
    }
    return [...byZone.entries()].map(([zoneId, items]) => ({
      zoneId,
      zoneName: zoneNames?.get(zoneId) ?? zoneId,
      items: [...items].sort((a, b) =>
        a.seat_number.localeCompare(b.seat_number, undefined, { numeric: true }),
      ),
    }));
  }, [seats, zoneNames]);

  if (seats.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {grouped.map(({ zoneId, zoneName, items }) => (
        <div key={zoneId} className="space-y-2">
          {grouped.length > 1 && (
            <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
              {zoneName}
            </p>
          )}
          {/* Seat numbers restart per zone, so "A-01" exists in every hall.
              The zone heading disambiguates them visually but is a sibling of
              the grid, not part of any button's name. */}
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {items.map((seat) => {
              const selected = selectedId === seat.seat_id;
              const disabled = !seat.available && !seat.assigned_to_me;
              const label = seat.assigned_to_me
                ? strings.yourSeat
                : seat.available
                  ? strings.seatAvailable
                  : strings.seatTaken;

              return (
                <motion.button
                  key={seat.seat_id}
                  type="button"
                  disabled={disabled}
                  aria-pressed={selected}
                  aria-label={
                    grouped.length > 1
                      ? `${seat.seat_number}, ${zoneName}, ${label}`
                      : `${seat.seat_number}, ${label}`
                  }
                  onClick={() => {
                    if (!disabled) onSelect(seat.seat_id);
                  }}
                  whileTap={reduce || disabled ? undefined : { scale: 0.97 }}
                  transition={{ duration: 0.12, ease: EASE_OUT }}
                  className={cn(
                    "font-display duration-120 flex aspect-square flex-col items-center justify-center rounded-xl border text-sm font-semibold tabular-nums transition-colors ease-out",
                    selected && "border-primary bg-primary/12 text-primary ring-primary/30 ring-2",
                    !selected &&
                      seat.available &&
                      "border-border/60 bg-card hover:bg-muted/40 active:bg-muted/60",
                    !selected &&
                      seat.assigned_to_me &&
                      "border-primary/40 bg-primary/8 text-primary",
                    disabled &&
                      !seat.assigned_to_me &&
                      "border-border/40 bg-muted/30 text-muted-foreground cursor-not-allowed opacity-60",
                  )}
                >
                  {seat.seat_number}
                </motion.button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
